import {
  addDays,
  departureTime,
  formatTimeSl,
  minutesToTime,
  timeToMinutes,
  weekdayOf,
  type LocalDate,
  type Occurrence,
} from '@planer/shared';

/** "6:00–7:30", or just "7:00" for instant habits. */
export function timeLabel(occ: Pick<Occurrence, 'startTime' | 'durationMin'>): string {
  if (occ.durationMin === 0) return formatTimeSl(occ.startTime);
  const end = minutesToTime(timeToMinutes(occ.startTime) + occ.durationMin);
  return `${formatTimeSl(occ.startTime)}–${formatTimeSl(end)}`;
}

/** 90 → "1 h 30 min", 45 → "45 min", 120 → "2 h". */
export function durationLabel(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h === 0) return `${rest} min`;
  return rest === 0 ? `${h} h` : `${h} h ${rest} min`;
}

/** Minutes until/since; "čez 25 min", "še 40 min". */
export function untilLabel(from: Date, to: Date): string {
  return durationLabel(Math.ceil((to.getTime() - from.getTime()) / 60000));
}

const WEEKDAYS = ['ponedeljek', 'torek', 'sreda', 'četrtek', 'petek', 'sobota', 'nedelja'];
const WEEKDAY_LETTERS = ['P', 'T', 'S', 'Č', 'P', 'S', 'N'];
const MONTHS = [
  'januar',
  'februar',
  'marec',
  'april',
  'maj',
  'junij',
  'julij',
  'avgust',
  'september',
  'oktober',
  'november',
  'december',
];

const WEEKDAY_SHORT = ['pon', 'tor', 'sre', 'čet', 'pet', 'sob', 'ned'];
/** "pon", "tor", … */
export const weekdayShort = (date: LocalDate): string => WEEKDAY_SHORT[weekdayOf(date) - 1] ?? '';
export const weekdayName = (date: LocalDate): string => WEEKDAYS[weekdayOf(date) - 1] ?? '';
export const weekdayLetter = (date: LocalDate): string =>
  WEEKDAY_LETTERS[weekdayOf(date) - 1] ?? '';
export const monthName = (date: LocalDate): string => MONTHS[Number(date.slice(5, 7)) - 1] ?? '';
export const dayNumber = (date: LocalDate): number => Number(date.slice(8, 10));

/** "četrtek, 1. oktober" */
export function longDate(date: LocalDate): string {
  return `${weekdayName(date)}, ${dayNumber(date)}. ${monthName(date)}`;
}

/** "Danes" / "Jutri" / "Včeraj" / "Sobota" */
export function relativeDayTitle(date: LocalDate, today: LocalDate): string {
  if (date === today) return 'Danes';
  if (date === addDays(today, 1)) return 'Jutri';
  if (date === addDays(today, -1)) return 'Včeraj';
  const name = weekdayName(date);
  return name.charAt(0).toUpperCase() + name.slice(1);
}

/** "28. 9. – 4. 10." */
export function rangeLabel(from: LocalDate, to: LocalDate): string {
  const d = (x: LocalDate) => `${dayNumber(x)}. ${Number(x.slice(5, 7))}.`;
  return `${d(from)} – ${d(to)}`;
}

/** ISO week number. */
export function isoWeek(date: LocalDate): number {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = Date.UTC(d.getUTCFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - yearStart) / 86400000 + 1) / 7);
}

/** "5. 10." (the year only when it is not the current one). */
export function shortDate(date: LocalDate, currentYear = new Date().getFullYear()): string {
  const base = `${dayNumber(date)}. ${Number(date.slice(5, 7))}.`;
  return Number(date.slice(0, 4)) === currentYear ? base : `${base} ${date.slice(0, 4)}`;
}

/** "pravkar", "pred 4 min", "pred 2 h", "1. 10." */
export function syncedAgo(iso: string | null, now = Date.now()): string {
  if (!iso) return 'še ni sinhronizirano';
  const min = Math.round((now - Date.parse(iso)) / 60000);
  if (min < 1) return 'posodobljeno pravkar';
  if (min < 60) return `posodobljeno pred ${min} min`;
  if (min < 24 * 60) return `posodobljeno pred ${Math.round(min / 60)} h`;
  const d = new Date(iso);
  return `posodobljeno ${d.getDate()}. ${d.getMonth() + 1}.`;
}

/** Slovene plural forms: [1, 2, 3–4, 5+] (e.g. termin, termina, termine, terminov). */
export function plural(n: number, forms: readonly [string, string, string, string]): string {
  const mod = Math.abs(n) % 100;
  const form =
    mod === 1 ? forms[0] : mod === 2 ? forms[1] : mod === 3 || mod === 4 ? forms[2] : forms[3];
  return `${n} ${form}`;
}

export const TERMIN = ['termin', 'termina', 'termine', 'terminov'] as const;

/** "odhod 7:35 · nazaj 40 min" for an occurrence with travel time, else null. */
export function travelLabel(
  o: Pick<Occurrence, 'startTime'> &
    Partial<Pick<Occurrence, 'travelBeforeMin' | 'travelAfterMin'>>,
): string | null {
  const leave = departureTime(o);
  const back = o.travelAfterMin ?? 0;
  if (!leave && back <= 0) return null;
  return [
    leave ? `odhod ${formatTimeSl(leave)}` : null,
    back > 0 ? `nazaj ${durationLabel(back)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** "pravkar", "pred 5 min", "pred 3 h", "včeraj", "pred 4 dnevi", "12. 3. 2026". */
export function agoLabel(iso: string, now = Date.now()): string {
  const min = Math.round((now - Date.parse(iso)) / 60000);
  if (min < 2) return 'pravkar';
  if (min < 60) return `pred ${min} min`;
  if (min < 24 * 60) return `pred ${Math.round(min / 60)} h`;
  const days = Math.round(min / (24 * 60));
  if (days === 1) return 'včeraj';
  if (days < 30) return `pred ${days} ${days === 2 ? 'dnevoma' : 'dnevi'}`;
  const d = new Date(iso);
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

/** "iPhone · Safari", "Mac · Chrome", … from a user agent string. */
export function deviceLabel(ua: string | null): string {
  if (!ua) return 'Neznana naprava';
  const os = /iPhone/.test(ua)
    ? 'iPhone'
    : /iPad/.test(ua)
      ? 'iPad'
      : /Android/.test(ua)
        ? 'Android'
        : /Macintosh|Mac OS X/.test(ua)
          ? 'Mac'
          : /Windows/.test(ua)
            ? 'Windows'
            : /Linux/.test(ua)
              ? 'Linux'
              : null;
  const browser = /Edg\//.test(ua)
    ? 'Edge'
    : /Firefox\/|FxiOS/.test(ua)
      ? 'Firefox'
      : /Chrome\/|CriOS/.test(ua)
        ? 'Chrome'
        : /Safari\//.test(ua)
          ? 'Safari'
          : null;
  return [os, browser].filter(Boolean).join(' · ') || 'Neznana naprava';
}
